import { TipoPermissao } from "constants/EnumTipoPermissao";
import { usePermissoesContext } from "context/permissoes/PermissoesContext";
import { createContext, useState, useContext } from "react";
import { OpcoesSelecionaveisCheckBox } from "types/OpcoesSelecionaveisCheckBoxType";
import { RegistroAcordoDTO } from "types/dto/RegistroAcordoDTO";
import { InadimplenciaDTO } from "types/dto/RegistroInadimplenciaDTO";

export type GestaoCobrancaCadastroProps = {
  condominioId: number | undefined;
  setCondominioId: (condominioId: number) => void;
  unidadeId: number | undefined;
  setUnidadeId: (unidadeId: number) => void;
  inadimplencias: InadimplenciaDTO[];
  inadimplenciasSelecionadas: number[];
  setInadimplencias: (inadimplencia: InadimplenciaDTO[]) => void;
  setInadimplenciasSelecionadas: (inadimplenciasIds: number[]) => void;
  acordos: RegistroAcordoDTO[];
  setAcordos: (acordos: RegistroAcordoDTO[]) => void;
  toggleCheckbox: (id: number) => void;
  selecionarTodasDaPagina: () => void;
  isEdicao: boolean;
  setIsEdicao: (isEdicao: boolean) => void;
  possuiPermissaoIniciarAcordo: boolean;
  possuiPermissaoEmitirAcordoDetalhado: boolean;
  possuiPermissaoEmitirConfissaoDeDivida: boolean;
  opcaoSelecionadaCheckBox: OpcoesSelecionaveisCheckBox;
  setOpcaoSelecionadaCheckBox: (opcao: OpcoesSelecionaveisCheckBox) => void;
};

const INITIAL_CONTEXT = {
  condominioId: undefined,
  setCondominioId: () => {},
  unidadeId: undefined,
  setUnidadeId: () => {},
  inadimplencias: [],
  inadimplenciasSelecionadas: [],
  setInadimplencias: () => {},
  setInadimplenciasSelecionadas: () => {},
  acordos: [],
  setAcordos: () => {},
  toggleCheckbox: () => {},
  selecionarTodasDaPagina: () => {},
  isEdicao: false,
  setIsEdicao: () => {},
  possuiPermissaoIniciarAcordo: false,
  possuiPermissaoEmitirAcordoDetalhado: false,
  possuiPermissaoEmitirConfissaoDeDivida: false,
  opcaoSelecionadaCheckBox: "NENHUMA" as OpcoesSelecionaveisCheckBox,
  setOpcaoSelecionadaCheckBox: () => {},
};

export const GestaoCobrancaCadastro = createContext<GestaoCobrancaCadastroProps>(INITIAL_CONTEXT);

export const GestaoCobrancaCadastroProvider = ({ children }: any) => {
  const [inadimplencias, setInadimplencias] = useState<InadimplenciaDTO[]>([]);
  const [inadimplenciasSelecionadas, setInadimplenciasSelecionadas] = useState<number[]>([]);
  const [acordos, setAcordos] = useState<RegistroAcordoDTO[]>([]);
  const [condominioId, setCondominioId] = useState<number>();
  const [unidadeId, setUnidadeId] = useState<number>();
  const [isEdicao, setIsEdicao] = useState<boolean>(false);
  const [opcaoSelecionadaCheckBox, setOpcaoSelecionadaCheckBox] = useState<OpcoesSelecionaveisCheckBox>("NENHUMA");
  const { usuarioPossuiPermissao } = usePermissoesContext();

  const toggleCheckbox = (id: number) => {
    if (inadimplenciasSelecionadas.includes(id)) {
      setInadimplenciasSelecionadas(inadimplenciasSelecionadas.filter((item) => item !== id));
    } else {
      setInadimplenciasSelecionadas([...inadimplenciasSelecionadas, id]);
    }
  };

  const selecionarTodasDaPagina = () => {
    const allEntidadeIds = inadimplencias.map((inadimplencia) => inadimplencia.id);

    setInadimplenciasSelecionadas([
      ...inadimplenciasSelecionadas,
      ...allEntidadeIds.filter((id) => !inadimplenciasSelecionadas.includes(id)),
    ]);
  };

  const adicionarInadimplencias = (inadimplencia: InadimplenciaDTO[]) => {
    setInadimplencias(inadimplencia);
  };

  const adicionarAcordos = (acordos: RegistroAcordoDTO[]) => {
    setAcordos(acordos);
  };

  const adicionarCondominioId = (condominioId: number) => {
    setCondominioId(condominioId);
  };

  const adicionarUnidadeId = (unidadeId: number) => {
    setUnidadeId(unidadeId);
  };

  const changeInadimplenciasSelecionadas = (inadimplenciasIds: number[]) => {
    setInadimplenciasSelecionadas(inadimplenciasIds);
  };

  const changeIsEdicao = (isEdicao: boolean) => {
    setIsEdicao(isEdicao);
  };

  const contextData: GestaoCobrancaCadastroProps = {
    condominioId,
    setCondominioId: adicionarCondominioId,
    unidadeId,
    setUnidadeId: adicionarUnidadeId,
    inadimplencias,
    inadimplenciasSelecionadas,
    setInadimplencias: adicionarInadimplencias,
    setInadimplenciasSelecionadas: changeInadimplenciasSelecionadas,
    acordos,
    setAcordos: adicionarAcordos,
    toggleCheckbox,
    selecionarTodasDaPagina,
    isEdicao,
    setIsEdicao: changeIsEdicao,
    opcaoSelecionadaCheckBox,
    setOpcaoSelecionadaCheckBox,

    possuiPermissaoIniciarAcordo: usuarioPossuiPermissao("iniciarAcordoGestaoCobranca", TipoPermissao.EXECUTAR),
    possuiPermissaoEmitirAcordoDetalhado: usuarioPossuiPermissao("relatorioDetalhadoAcordo", TipoPermissao.EXECUTAR),
    possuiPermissaoEmitirConfissaoDeDivida: usuarioPossuiPermissao(
      "relatorioTermoDeAcordoConfissaoDeDivida",
      TipoPermissao.EXECUTAR
    ),
  };

  return <GestaoCobrancaCadastro.Provider value={contextData}>{children}</GestaoCobrancaCadastro.Provider>;
};

export function useGestaoCobrancaCadastroContext() {
  return useContext(GestaoCobrancaCadastro);
}
